import type {CheckboxTreeOptionType} from 'platform/components';

import type {EagColumnOrGroup} from '../../../../../types/EagColumnGroup';
import {isEagColumnGroup} from '../../../../../utils/isEagColumnGroup';

/**
 * Transforms columnConfig into CheckboxTree option tree for grouped column panel.
 *
 * Groups are collected using a Map for O(1) child-merge. The same groupId
 * may appear multiple times in columnConfig when preset sort order interleaves
 * columns from different groups — children from all instances are merged.
 *
 * Ungrouped top-level EagColumn entries become flat leaf options.
 */
export const buildCheckboxTreeOptions = (
  columnConfig: EagColumnOrGroup[]
): CheckboxTreeOptionType[] => {
  const result: CheckboxTreeOptionType[] = [];
  const groupMap = new Map<string, CheckboxTreeOptionType>();

  for (const item of columnConfig) {
    if (isEagColumnGroup(item)) {
      const newChildren = item.children.map(
        (child): CheckboxTreeOptionType => ({value: child.key, label: child.headerName})
      );
      const existing = groupMap.get(item.groupId);
      if (existing) {
        existing.options!.push(...newChildren);
      } else {
        const entry: CheckboxTreeOptionType = {
          value: item.groupId,
          label: item.headerName,
          options: newChildren,
        };
        groupMap.set(item.groupId, entry);
        result.push(entry);
      }
    } else {
      result.push({value: item.key, label: item.headerName});
    }
  }

  return result;
};
